import React, { useState, useEffect } from "react";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  usePagination,
} from "react-table";
import { matchSorter } from "match-sorter"; // A great library for fuzzy filtering/sorting items
import {
  Badge,
  Box,
  IconButton,
  Center,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  Spinner,
  Heading,
} from "@chakra-ui/react";
import {
  SearchIcon,
  UpDownIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from "@chakra-ui/icons";
import {
  BsChevronDoubleLeft,
  BsChevronLeft,
  BsChevronRight,
  BsChevronDoubleRight,
} from "react-icons/bs";

import { isAnnounced, upToDate, programYear } from './constants';

const portAwards = ['Honorable Mention', 'Silver Medal', 'Gold Medal']

// Default UI for filtering
function GlobalFilter({ globalFilter, setGlobalFilter }) {
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <Center w="500px">
      <InputGroup>
        <InputLeftElement
          top="4px"
          pointerEvents="none"
          children={<SearchIcon size="lg" color="teal.200" />}
        />
        <Input
          width="100%"
          size="lg"
          borderColor="teal.300"
          value={value || ""}
          onChange={(e) => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder={`Search National Awards...`}
        />
      </InputGroup>
    </Center>
  );
}
function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

// Our table component
function Medals({ columns, data }) {
  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    // rows,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    state,
    // visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      // defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: { pageIndex: 0, pageSize: 100 },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <VStack align="stretch">
      <Heading m={5}><Center>{programYear} National Medalists</Center></Heading>
      <Text mb={6} fontStyle="italic" fontWeight="bold"><Center>{upToDate} {isAnnounced ? programYear : programYear - 1}.</Center></Text>
      <Center>
        {/* Filter */}

        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
      </Center>
      <Flex justifyContent="space-between">
        {/* Totals */}

        <HStack>
          <IconButton
            aria-label="Go to First Page"
            icon={<BsChevronDoubleLeft />}
            size="large"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          />

          <IconButton
            aria-label="Go to Previous Page"
            icon={<BsChevronLeft />}
            size="large"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          />

          <Text fontSize="12px" whiteSpace="nowrap">
            <strong>
              Page {pageIndex + 1} of {pageOptions.length}
            </strong>
          </Text>

          <IconButton
            aria-label="Go to Next Page"
            icon={<BsChevronRight />}
            size="large"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          />

          <IconButton
            aria-label="Go to Last Page"
            icon={<BsChevronDoubleRight />}
            size="large"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          />
        </HStack>

        <HStack variant="outline">
          <Text whiteSpace="nowrap">Display:</Text>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[50, 100, 200].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </Select>
        </HStack>
      </Flex>
      {/* 
        Table
      */}
      <Table {...getTableProps()}>
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <Th
                  whiteSpace="nowrap"
                  bgColor="teal.500"
                  color="white"
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render("Header")}&nbsp;
                  <Box as="span">
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <TriangleDownIcon
                          aria-label="sorted descending"
                          pl={1}
                        />
                      ) : (
                        <TriangleUpIcon aria-label="sorted ascending" pl={1} />
                      )
                    ) : (
                      <UpDownIcon pl={1} boxSize={3.5} />
                    )}
                  </Box>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <Td {...cell.getCellProps()}>{cell.render("Cell")}</Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {/* Bottom Pagination */}
      <Flex justifyContent="space-between">
        <HStack>
          <IconButton
            aria-label="Go to First Page"
            icon={<BsChevronDoubleLeft />}
            size="large"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          />

          <IconButton
            aria-label="Go to Previous Page"
            icon={<BsChevronLeft />}
            size="large"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          />

          <Text fontSize="12px" whiteSpace="nowrap">
            <strong>
              Page {pageIndex + 1} of {pageOptions.length}
            </strong>
          </Text>

          <IconButton
            aria-label="Go to Next Page"
            icon={<BsChevronRight />}
            size="large"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          />

          <IconButton
            aria-label="Go to Last Page"
            icon={<BsChevronDoubleRight />}
            size="large"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          />
        </HStack>

        <HStack variant="outline">
          <Text whiteSpace="nowrap">Display:</Text>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[50, 100, 200].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </Select>
        </HStack>
      </Flex>
    </VStack>
  );
}

// Define a custom filter filter function!
function filterGreaterThan(rows, id, filterValue) {
  return rows.filter((row) => {
    const rowValue = row.values[id];
    return rowValue >= filterValue;
  });
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = (val) => typeof val !== "number";

export default function AwardsTable() {
  const [awardsData, setAwardsData] = useState();
  // const [lastModified, setLastModified] = useState();

  useEffect(() => {
    const fetchData = async () => {
      await fetch(process.env.REACT_APP_AWARDS_API, {
        method: "GET",
        // credentials: "same-origin",
        agent: null,
        mode: "cors",
        headers: {
          Accept: "application/json",
          // 'Access-Control-Allow-Origin': '*',
          // 'Access-Control-Allow-Methods': '*'
        },
      }).then(function (response) {
        response.json().then(function (data) {

          const winners = data.data;

          const updatedResults = winners.map(result => {

            const portTitle = result.workType.charAt(0).toUpperCase() + result.workType.slice(1);
            const nationalPortFormatted = [portTitle + ' ' + result.nationalAwards[0]]

            if (result.workType === 'portfolio' && portAwards.includes(result.nationalAwards[0])) {

              return {
                ...result,      // Copy all the existing properties
                nationalAwards: nationalPortFormatted // Update the award portfolio property
              };
            }
            return result; // Return the object unchanged if the condition is not met
          });

          setAwardsData(updatedResults);
        });

      });
    };

    fetchData();
  }, []);

  // Custom component to render Genres
  const AwardBadges = ({ values }) => {



    // Loop through the array and create a badge-like component instead of a comma-separated string
    return (
      <>
        {values.map((award, idx) => {
          if (award === "Gold Medal" || award === "Portfolio Gold Medal") {
            return (
              <Badge variant="solid" bgColor="#ffb000" key={idx} mr={2} mb={2}>
                {award}
              </Badge>
            );
          } else if (
            award === "Silver Medal" ||
            award === "Portfolio Silver Medal"
          ) {
            return (
              <Badge variant="solid" bgColor="#9ea1a2" key={idx} mr={2} mb={2}>
                {award}
              </Badge>
            );
          } else if (
            award === "American Voices Medal" ||
            award === "American Visions Medal"
          ) {
            return (
              <Badge
                colorScheme="blue"
                variant="outline"
                key={idx}
                mr={2}
                mb={2}
              >
                {award}
              </Badge>
            );
          } else if (
            award === "Portfolio Honorable Mention" 
          ) {
            return (
              <Badge

                bgColor='#CD7F32'
                color='#FFFFFF'
                key={idx}
                mr={2}
                mb={2}
              >
                {award}
              </Badge>
            );
          } else {
            return (
              <Badge
                colorScheme="green"
                variant="outline"
                key={idx}
                mr={2}
                mb={2}
              >
                {award}
              </Badge>
            );
          }
        })}
      </>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        id: "name",
        accessor: (row) => `${row.participantFirstName} ${row.participantLastName}`,
      },
      {
        Header: "School",
        accessor: "schoolName",
      },
      {
        Header: "City",
        accessor: "schoolCity",
      },
      {
        Header: "State",
        accessor: "schoolState",
      },
      {
        Header: "Title",
        accessor: "workTitle",
      },
      {
        Header: "Awards",
        accessor: "nationalAwards",
        Cell: ({ cell: { value } }) => <AwardBadges values={value} />,
      },
    ],
    []
  );

  const awardData = React.useMemo(() => awardsData, [awardsData]);

  if (awardData) {
    // const cleanedAwardsData = awardData.map((nationalAwards, workType, idx) => {

    //   console.log('🙈 ~ cleanedAwardsData ~  idx:', idx)

    //   return null

    // })

    // console.log('🙈 ~ AwardsTable ~ awardData:', cleanedAwardsData)

    return <Medals columns={columns} data={awardData} />;
  } else {
    return (
      <VStack>
        <Spinner boxSize={100} m={100} />
      </VStack>
    );
  }
}
